import React, { useState, useMemo, useEffect } from "react";
import { useSiteAnalytics } from "../hooks/useSiteAnalytics";
import { useTrendAnalytics } from "../hooks/useTrendAnalytics";
import { useMonthlyReport } from "../hooks/useMonthlyReport";
import { useInvoiceReport } from "../hooks/useInvoiceReport";
import { useInvoiceTrends } from "../hooks/useInvoiceTrends";
import { useReportSettings } from "../hooks/useReportSettings";
import ProviderSpendingChart from "../components/analytics/ProviderSpendingChart";
import CategorySpendingChart from "../components/analytics/CategorySpendingChart";
import TopSitesList from "../components/analytics/TopSitesList";
import MonthlySpendingLineChart from "../components/analytics/MonthlySpendingLineChart";
import MonthYearSelector from "../components/analytics/MonthYearSelector";
import { formatCurrency } from "../utils/helpers";
import { SpinnerCustom } from "../components/ui/Spinner";
import { canMutateTenantData } from "../config/constants";
import ReportBudgetCard from "../components/report20/ReportBudgetCard";

import CustomSelect from "../components/ui/CustomSelect";
const MONTH_NAMES = ["January", "February", "March", "April", "May", "June", "July", "August", "September", "October", "November", "December"];

// function getPriorMonthYear() {
//   const d = new Date();
//   d.setMonth(d.getMonth() - 1);
//   return { year: d.getFullYear(), month: d.getMonth() + 1 };
// }

function getPriorMonthYear() {
  const now = new Date();

  // Convert to UTC-safe date (no timezone shift)
  const utc = new Date(Date.UTC(
    now.getUTCFullYear(),
    now.getUTCMonth(),
    now.getUTCDate()
  ));

  utc.setUTCMonth(utc.getUTCMonth() - 1);

  return {
    year: utc.getUTCFullYear(),
    month: utc.getUTCMonth() + 1,
  };
}

const AnalyticsPage = ({
  tenant,
  tenantId,
  userRole,
  setHeaderOptionsContent,
}) => {
  const canEditReportBudget = canMutateTenantData(userRole);
  const { year: defaultYear, month: defaultMonth } = getPriorMonthYear();
  const [selectedYear, setSelectedYear] = useState(defaultYear);
  const [selectedMonth, setSelectedMonth] = useState(defaultMonth);
  const [months, setMonths] = useState(6);

  // Report month: when user picks "Current" we use current year/month; otherwise selected
  const now = new Date();
  const currentYear = now.getFullYear();
  const currentMonth = now.getMonth() + 1;
  const isCurrentMonthSelected = selectedYear === null && selectedMonth === null;
  const reportYear = selectedYear ?? currentYear;
  const reportMonth = selectedMonth ?? currentMonth;
  const isReportCurrentMonth = reportYear === currentYear && reportMonth === currentMonth;

  // Set header options to Analytics bar (Telecom Expense Management System title)
  useEffect(() => {
    if (!setHeaderOptionsContent) return;
    setHeaderOptionsContent(
      <div className="flex justify-between items-center w-full">
        <div className="telecom-title-wrap">
          <h1 className="client-logo-title">Telecom Expense Management System</h1>
          <p className="telecom-title-tagline">Cost optimization made easy</p>
        </div>
      </div>
    );
    return () => setHeaderOptionsContent(null);
  }, [setHeaderOptionsContent]);

  const isHistorical = selectedYear !== null && selectedMonth !== null;

  // Current analytics (fallback for sites when needed)
  const { data: currentData } = useSiteAnalytics(tenantId);

  // Invoice-based report for the displayed month (primary source for totals and provider breakdown)
  const { data: invoiceReportData, loading: invoiceReportLoading, error: invoiceReportError, refresh: refreshInvoiceReport } = useInvoiceReport(
    tenantId,
    reportYear,
    reportMonth
  );

  // Invoice-based trends (matches top-of-page totals)
  const { data: invoiceTrendData, loading: invoiceTrendLoading } = useInvoiceTrends(tenantId, months);

  // Report settings (annual budget for variance) – set/edit on Reports tab
  const { annualBudgetByYear, refresh: refreshReportSettings, updateBudget } = useReportSettings();

  // Legacy snapshot report (still used for drill-down when invoice report has snapshot_data)
  const { data: historicalData } = useMonthlyReport(tenantId, selectedYear, selectedMonth);

  const trendData = invoiceTrendData;
  const trendLoading = invoiceTrendLoading;

  // Display data: prefer invoice report when available; structure matches what charts expect
  const displayData = useMemo(() => {
    if (invoiceReportData) {
      return {
        totalSpent: invoiceReportData.totalSpent,
        totalSites: invoiceReportData.totalSites,
        totalCircuits: invoiceReportData.totalCircuits ?? invoiceReportData.dataCircuitsCount,
        providerSpending: invoiceReportData.providerSpending || [],
        topSites: invoiceReportData.topSites || [],
        allSites: invoiceReportData.allSites || [],
        snapshot_data: invoiceReportData.snapshot_data,
        dataCircuitsCount: invoiceReportData.dataCircuitsCount,
      };
    }
    if (isHistorical && historicalData) {
      return {
        ...historicalData,
        dataCircuitsCount: historicalData.totalCircuits,
      };
    }
    return currentData ? { ...currentData, dataCircuitsCount: currentData.totalCircuits } : null;
  }, [invoiceReportData, isHistorical, historicalData, currentData]);

  const loading = invoiceReportLoading;
  const error = invoiceReportError;

  const handleMonthYearChange = (year, month) => {
    setSelectedYear(year);
    setSelectedMonth(month);
  };

  const snapshotDataForDrillDown = displayData?.snapshot_data ?? null;

  const summaryMetrics = useMemo(() => {
    if (!displayData) return { totalSpend: 0, totalSites: 0, circuits: null, dataCircuitsCount: null };
    const providerSpending = displayData.providerSpending || [];
    const totalSpend =
      displayData.totalSpent ??
      displayData.total_mrc ??
      providerSpending.reduce((sum, p) => sum + (parseFloat(p.totalMRC) || 0), 0);
    const totalSites =
      displayData.totalSites ??
      (Array.isArray(displayData.allSites) ? displayData.allSites.length : 0);
    const dataCircuitsCount = displayData.dataCircuitsCount ?? null;
    return { totalSpend, totalSites, circuits: null, dataCircuitsCount };
  }, [displayData]);

  // Circuits card only: use original source (snapshot or site analytics), not invoice report
  const circuitsForCard = useMemo(() => {
    if (isHistorical && historicalData != null) {
      const fromSnapshot = historicalData.totalCircuits ?? historicalData.total_circuits;
      if (fromSnapshot != null) return fromSnapshot;
      if (Array.isArray(historicalData.snapshot_data)) return historicalData.snapshot_data.length;
      return null;
    }
    if (currentData != null) {
      return currentData.totalCircuits ?? currentData.total_circuits ?? null;
    }
    return null;
  }, [isHistorical, historicalData, currentData]);

  // Spend overview section only: use original source (snapshot or site analytics), not invoice report
  const spendOverviewData = useMemo(() => {
    if (isHistorical && historicalData != null) {
      return {
        providerSpending: historicalData.providerSpending || [],
        categorySpending: historicalData.categorySpending || [],
      };
    }
    if (currentData != null) {
      return {
        providerSpending: currentData.providerSpending || [],
        categorySpending: currentData.categorySpending || [],
      };
    }
    return { providerSpending: [], categorySpending: [] };
  }, [isHistorical, historicalData, currentData]);

  const budgetVariance = useMemo(() => {
    const budget = annualBudgetByYear && reportYear ? parseFloat(annualBudgetByYear[String(reportYear)]) : null;
    if (budget == null || Number.isNaN(budget) || !invoiceTrendData?.trends?.length) return null;
    const ytd = invoiceTrendData.trends
      .filter((t) => {
        const [y] = (t.date || "").split("-");
        const m = parseInt((t.date || "").split("-")[1], 10);
        return parseInt(y, 10) === reportYear && m <= reportMonth;
      })
      .reduce((sum, t) => sum + (parseFloat(t.total_mrc) || 0), 0);
    return { budget, ytd, variance: budget - ytd };
  }, [annualBudgetByYear, reportYear, reportMonth, invoiceTrendData]);

  if (loading && !displayData) {
    return (
      <div className="analytics-page pt-[155px] min-h-screen bg-gradient-to-b from-gray-50 to-white flex items-center justify-center">
        <div className="flex flex-col items-center">
          <SpinnerCustom />
        </div>
      </div>
    );
  }

  if (error && !displayData) {
    return (
      <div className="analytics-page pt-[155px] min-h-screen bg-gradient-to-b from-gray-50 to-white flex items-center justify-center p-4">
        <div className="bg-white rounded-xl shadow-md border border-gray-100 p-8 max-w-md text-center">
          <p className="text-sm font-medium text-red-600 mb-3">
            {error.message || "Failed to load reports data"}
          </p>
          <p className="text-xs text-gray-500 mb-4">
            No invoice data for the selected month. Select a different month or try again.
          </p>
          <button
            onClick={() => refreshInvoiceReport && refreshInvoiceReport()}
            className="inline-flex items-center justify-center px-4 py-2 text-sm font-medium rounded-lg shadow-sm text-white bg-blue-600 hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500"
          >
            Retry
          </button>
        </div>
      </div>
    );
  }

  if (!displayData) {
    return (
      <div className="analytics-page pt-[155px] min-h-screen bg-gradient-to-b from-gray-50 to-white flex items-center justify-center p-4">
        <div className="bg-white rounded-xl shadow-md border border-gray-100 p-8 text-center">
          <p className="text-sm text-gray-500">No reports data available</p>
        </div>
      </div>
    );
  }

  return (
    <div className="analytics-page pt-[155px] min-h-screen bg-gradient-to-b from-gray-50 to-white">
      <div className="max-w-full lg:max-w-[85%] mx-4 sm:mx-5 lg:mx-auto mt-10 pb-12">
      {/* Reports Dashboard header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4 mb-6 bg-white rounded-xl shadow-md border border-gray-100 px-5 py-5">
        <div>
          <h1 className="text-xl font-bold text-gray-900 tracking-tight">Reports Dashboard</h1>
          <p className="text-sm text-gray-500 mt-0.5">View spend by provider, category, and trends</p>
        </div>
        <MonthYearSelector
          onChange={handleMonthYearChange}
          selectedYear={selectedYear}
          selectedMonth={selectedMonth}
          showCurrentOption={true}
        />
      </div>

      {/* Banner: data period and current-month warning */}
      <div className="mb-5 bg-blue-50 border border-blue-100 rounded-xl px-4 py-3 shadow-sm">
        <p className="text-sm text-blue-800">
          Data shown for {MONTH_NAMES[reportMonth - 1]} {reportYear}.
          {isReportCurrentMonth && (
            <span className="font-medium"> Data for the current month may still be incomplete and changing until the final set of invoices is in.</span>
          )}
        </p>
      </div>

      {/* Summary cards */}
      <div className="grid grid-cols-1 sm:grid-cols-3 gap-5 mb-6">
        <div className="bg-white rounded-xl shadow-md border border-gray-100 border-l-4 border-l-blue-600 px-5 py-5 flex flex-col min-h-[96px] hover:shadow-lg transition-shadow">
          <p className="text-xs font-semibold text-blue-600 uppercase tracking-wider">TOTAL SPEND YTD</p>
          <p className="text-xl font-bold text-blue-700 mt-2 tabular-nums">
            {formatCurrency(summaryMetrics.totalSpend)}
          </p>
          <p className="text-xs text-gray-500 mt-2">Sum of invoices for this month (by invoice date)</p>
          {budgetVariance != null && (
            <p className="text-xs text-gray-600 mt-2 tabular-nums">
              Budget {reportYear}: {formatCurrency(budgetVariance.budget)} · YTD: {formatCurrency(budgetVariance.ytd)} · Variance: {formatCurrency(budgetVariance.variance)}
            </p>
          )}
        </div>
        <div className="bg-white rounded-xl shadow-md border border-gray-100 border-l-4 border-l-emerald-600 px-5 py-5 flex flex-col min-h-[96px] hover:shadow-lg transition-shadow">
          <p className="text-xs font-semibold text-emerald-600 uppercase tracking-wider">Total sites</p>
          <p className="text-xl font-bold text-emerald-700 mt-2 tabular-nums">{summaryMetrics.totalSites}</p>
          <p className="text-xs text-gray-500 mt-2">Unique locations</p>
        </div>
        <div className="bg-white rounded-xl shadow-md border border-gray-100 border-l-4 border-l-violet-600 px-5 py-5 flex flex-col min-h-[96px] hover:shadow-lg transition-shadow">
          <p className="text-xs font-semibold text-violet-600 uppercase tracking-wider">Data Circuits</p>
          <p className="text-xl font-bold text-violet-700 mt-2 tabular-nums">
            {circuitsForCard != null ? circuitsForCard.toLocaleString() : "—"}
          </p>
          <p className="text-xs text-gray-500 mt-2"> In Service Data Circuits</p>
        </div>
      </div>

      {/* Loading overlay when refreshing report */}
      {loading && displayData && (
        <div className="mb-5 bg-blue-50 border border-blue-100 rounded-xl px-4 py-3 shadow-sm">
          <div className="flex items-center justify-center">
            <SpinnerCustom />
          </div>
        </div>
      )}

      {/* Spend overview: by provider and category (original – snapshot/site analytics only) */}
      <div className="bg-white rounded-xl shadow-md border border-gray-100 overflow-hidden mb-6">
        <div className="px-6 py-5 border-b border-gray-100 bg-gradient-to-r from-gray-50 to-white">
          <div className="flex items-center gap-3">
            <div className="flex h-10 w-10 items-center justify-center rounded-lg bg-blue-100 text-blue-600">
              <svg className="h-5 w-5" fill="none" stroke="currentColor" viewBox="0 0 24 24" aria-hidden>
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 19v-6a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2a2 2 0 002-2zm0 0V9a2 2 0 012-2h2a2 2 0 012 2v10m-6 0a2 2 0 002 2h2a2 2 0 002-2m0 0V5a2 2 0 012-2h2a2 2 0 012 2v14a2 2 0 01-2 2h-2a2 2 0 01-2-2z" />
              </svg>
            </div>
            <div>
              <h2 className="text-lg font-bold text-gray-900 tracking-tight">Spend overview</h2>
              <p className="text-sm text-gray-600 mt-0.5">By provider and category</p>
            </div>
          </div>
        </div>
        <div className="grid grid-cols-1 lg:grid-cols-2 divide-y lg:divide-y-0 lg:divide-x divide-gray-100">
          <div className="p-6 min-h-[280px] flex flex-col">
            <ProviderSpendingChart data={spendOverviewData.providerSpending} />
          </div>
          <div className="p-6 min-h-[280px] flex flex-col">
            <CategorySpendingChart data={spendOverviewData.categorySpending} />
          </div>
        </div>
      </div>

      {/* Trends Section */}
      <div className="mb-6">
        <div className="bg-white rounded-xl shadow-md border border-gray-100 overflow-hidden">
          <div className="px-6 py-5 border-b border-gray-100 bg-gradient-to-r from-gray-50 to-white flex flex-wrap items-center justify-between gap-4">
            <div className="flex items-center gap-3">
              <div className="flex h-10 w-10 items-center justify-center rounded-lg bg-emerald-100 text-emerald-600">
                <svg className="h-5 w-5" fill="none" stroke="currentColor" viewBox="0 0 24 24" aria-hidden>
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 7h8m0 0v8m0-8l-8 8-4-4-6 6" />
                </svg>
              </div>
              <div>
                <h2 className="text-lg font-bold text-gray-900 tracking-tight">Trends Reports</h2>
                <p className="text-sm text-gray-600 mt-0.5">Monthly spending over time</p>
              </div>
            </div>
            <CustomSelect
              value={months}
              onChange={(e) => setMonths(parseInt(e.target.value))}
              className="px-4 py-2.5 border border-gray-200 rounded-lg text-sm font-medium text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-[var(--muted,#6b6a66)]/20 focus:border-[var(--muted,#6b6a66)] shadow-sm transition-colors"
            >
              <option value={3}>Last 3 months</option>
              <option value={6}>Last 6 months</option>
            </CustomSelect>
          </div>
          <div className="p-6">
          {trendLoading ? (
            <div className="text-center py-12 flex justify-center">
              <SpinnerCustom />
            </div>
          ) : trendData?.trends && trendData.trends.length > 0 ? (
              <MonthlySpendingLineChart data={trendData.trends} />
          ) : (
            <div className="text-center py-12 px-4 rounded-xl bg-gray-50/80 border border-gray-100">
              <p className="text-sm text-gray-600">No trend data available.</p>
              <p className="text-xs text-gray-500 mt-1">Snapshots will appear here once they are created.</p>
            </div>
          )}
          </div>
        </div>
      </div>

      {/* Top Sites List with Drill-down */}
      <div className="mb-6">
        <TopSitesList
          topSites={displayData.topSites || []}
          allSites={displayData.allSites || []}
          snapshotData={snapshotDataForDrillDown}
        />
      </div>
      </div>
    </div>
  );
};

export default AnalyticsPage;
